import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  FileText,
  Image as ImageIcon,
  File as FileIcon,
  FileSpreadsheet,
  Download,
  Trash2,
  Upload,
  Loader2,
  Folder,
  FolderOpen,
  FolderPlus,
  ChevronRight,
  ArrowLeft,
  Pencil,
  MoveRight,
  Eye,
  Check,
  X,
  FolderInput,
  Home,
  MoreHorizontal,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Tip } from "@/components/Tip";
import { MarkdownBlock } from "@/components/MarkdownBlock";
import JSZip from "jszip";
import {
  fetchProjectFiles,
  fetchProjectFolders,
  createProjectFolder,
  renameProjectFolder,
  deleteProjectFolder,
  moveProjectFolder,
  uploadProjectFile,
  deleteProjectFile,
  updateProjectFile,
  downloadProjectFile,
  previewProjectFile,
  projectNotFoundMessage,
  isProjectNotFound,
  ProjectFileItem,
  ProjectFolderItem,
  ProjectFilesPage,
} from "@/lib/api/projects";

// ---------- formatting helpers (unchanged from Phase 5) ----------

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

function fmtDate(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function ext(name: string): string {
  const i = name.lastIndexOf(".");
  return i > 0 ? name.slice(i + 1).toUpperCase() : "FILE";
}

function isMarkdown(f: ProjectFileItem): boolean {
  const n = f.name.toLowerCase();
  return n.endsWith(".md") || n.endsWith(".markdown") || f.mime === "text/markdown";
}
function isPdf(f: ProjectFileItem): boolean {
  return f.name.toLowerCase().endsWith(".pdf") || f.mime === "application/pdf";
}
function isDocx(f: ProjectFileItem): boolean {
  return f.name.toLowerCase().endsWith(".docx") || f.mime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
}
function isPreviewable(f: ProjectFileItem): boolean {
  return isMarkdown(f) || isPdf(f) || isDocx(f);
}

function fileIcon(kind: string, name: string) {
  const n = name.toLowerCase();
  if (kind === "image") return <ImageIcon size={16} className="text-accent shrink-0" />;
  if (kind === "pdf" || n.endsWith(".pdf")) return <FileText size={16} className="text-red-400 shrink-0" />;
  if (n.endsWith(".docx") || n.endsWith(".doc")) return <FileText size={16} className="text-blue-400 shrink-0" />;
  if (/\.(xlsx?|csv)$/.test(n)) return <FileSpreadsheet size={16} className="text-emerald-400 shrink-0" />;
  if (n.endsWith(".md") || n.endsWith(".markdown")) return <FileText size={16} className="text-accent shrink-0" />;
  return <FileIcon size={16} className="text-secondary shrink-0" />;
}

// ---------- DOCX read-only renderer (JSZip -> document.xml -> DOM) ----------
// SECURITY: we build the DOM with text nodes only — never innerHTML, never
// eval/exec. The OOXML is parsed into elements; embedded macros/scripts are
// inert XML strings and are never executed. On any parse failure we throw so
// the caller can show the controlled "preview unavailable" fallback.

async function docxToHtmlEl(blob: Blob): Promise<HTMLElement> {
  const zip = await JSZip.loadAsync(blob);
  const docFile = zip.file("word/document.xml");
  if (!docFile) throw new Error("no-document");
  const xml = await docFile.async("string");
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, "application/xml");
  if (doc.querySelector("parsererror")) throw new Error("bad-xml");

  // Namespace-aware helpers.
  const W = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";
  const q = (tag: string) => doc.getElementsByTagNameNS(W, tag);

  const root = document.createElement("div");
  root.className = "docx-preview max-h-full overflow-auto";

  // Walk top-level body children in document order.
  const body = doc.getElementsByTagNameNS(W, "body")[0] || doc.documentElement;
  const walk = (node: Element, container: HTMLElement) => {
    const tag = node.localName;
    if (tag === "p") {
      const p = document.createElement("p");
      p.style.margin = "0 0 0.4em";
      // paragraphs can contain runs and hyperlinks
      for (const child of Array.from(node.children)) {
        if (child.localName === "r") {
          const run = document.createElement("span");
          const t = child.getElementsByTagNameNS(W, "t")[0];
          const br = child.getElementsByTagNameNS(W, "br")[0];
          const tab = child.getElementsByTagNameNS(W, "tab")[0];
          if (t) run.textContent = t.textContent || "";
          if (br) {
            run.appendChild(document.createElement("br"));
          }
          if (tab) run.appendChild(document.createTextNode("\t"));
          // bold / italic
          const rpr = child.getElementsByTagNameNS(W, "rPr")[0];
          if (rpr) {
            if (rpr.getElementsByTagNameNS(W, "b")[0]) run.style.fontWeight = "600";
            if (rpr.getElementsByTagNameNS(W, "i")[0]) run.style.fontStyle = "italic";
          }
          p.appendChild(run);
        } else if (child.localName === "hyperlink") {
          const a = document.createElement("span");
          a.style.color = "#22d3ee";
          for (const r of Array.from(child.children)) {
            if (r.localName === "r") {
              const t = r.getElementsByTagNameNS(W, "t")[0];
              if (t) a.appendChild(document.createTextNode(t.textContent || ""));
            }
          }
          p.appendChild(a);
        }
      }
      if (p.textContent?.trim() === "") p.style.minHeight = "0.8em";
      container.appendChild(p);
    } else if (tag === "tbl") {
      const table = document.createElement("table");
      table.style.borderCollapse = "collapse";
      table.style.margin = "0.4em 0";
      table.style.fontSize = "0.85em";
      for (const tr of Array.from(node.getElementsByTagNameNS(W, "tr"))) {
        const row = document.createElement("tr");
        for (const tc of Array.from(tr.getElementsByTagNameNS(W, "tc"))) {
          const cell = document.createElement("td");
          cell.style.border = "1px solid rgba(255,255,255,0.12)";
          cell.style.padding = "4px 8px";
          const inner = document.createElement("div");
          for (const p of Array.from(tc.getElementsByTagNameNS(W, "p"))) {
            walk(p, inner);
          }
          cell.appendChild(inner);
          row.appendChild(cell);
        }
        table.appendChild(row);
      }
      container.appendChild(table);
    } else if (tag === "sectPr") {
      // section properties — ignore
    }
  };
  for (const child of Array.from(body.children)) {
    walk(child, root);
  }
  return root;
}

// ---------- preview modal content ----------

function PreviewBody({
  projectId,
  file,
}: {
  projectId: string;
  file: ProjectFileItem;
}) {
  const [state, setState] = useState<{ phase: "loading" | "md" | "pdf" | "docx" | "fallback"; url?: string; text?: string; el?: HTMLElement | null; error?: string }>({
    phase: "loading",
  });
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  // Fetch bytes once; branch by type. Always revoke object URLs on cleanup.
  useEffect(() => {
    let objUrl: string | null = null;
    let cancelled = false;
    (async () => {
      try {
        const blob = await previewProjectFile(projectId, file.id);
        if (cancelled) return;
        if (isMarkdown(file)) {
          const text = await blob.text();
          setState({ phase: "md", text });
        } else if (isPdf(file)) {
          objUrl = URL.createObjectURL(blob);
          setPdfUrl(objUrl);
          setState({ phase: "pdf", url: objUrl });
        } else if (isDocx(file)) {
          try {
            const el = await docxToHtmlEl(blob);
            if (cancelled) return;
            setState({ phase: "docx", el });
          } catch {
            if (!cancelled) setState({ phase: "fallback", error: "Не удалось разобрать DOCX для предпросмотра." });
          }
        } else {
          if (!cancelled) setState({ phase: "fallback", error: "Предпросмотр недоступен для этого формата." });
        }
      } catch (e) {
        if (!cancelled) setState({ phase: "fallback", error: e instanceof Error ? e.message : "Не удалось загрузить файл для предпросмотра." });
      }
    })();
    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
      setPdfUrl(null);
    };
  }, [projectId, file.id]);

  if (state.phase === "loading") {
    return (
      <div className="py-16 flex justify-center text-tertiary">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }
  if (state.phase === "md" && state.text != null) {
    return (
      <div className="max-h-[65vh] overflow-auto -m-4 p-4">
        <MarkdownBlock content={state.text} />
      </div>
    );
  }
  if (state.phase === "pdf" && state.url) {
    return (
      <iframe
        title={file.name}
        src={state.url}
        className="w-full h-[65vh] rounded-lg bg-black/20 border border-strongline"
      />
    );
  }
  if (state.phase === "docx" && state.el) {
    // state.el is a detached DOM node; mount it via a ref container.
    return <DocxContainer el={state.el} />;
  }
  // fallback
  return (
    <div className="py-12 flex flex-col items-center gap-3 text-center">
      <FileIcon size={30} className="text-tertiary" />
      <p className="text-sm text-secondary">{state.error || "Предпросмотр недоступен."}</p>
      <p className="text-xs text-tertiary">Скачайте файл, чтобы открыть его в подходящем приложении.</p>
    </div>
  );
}

/** Mounts a pre-built (detached) DOM node safely via ref — no innerHTML. */
function DocxContainer({ el }: { el: HTMLElement }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    c.innerHTML = "";
    c.appendChild(el);
    return () => {
      c.innerHTML = "";
    };
  }, [el]);
  return (
    <div className="max-h-[65vh] overflow-auto -m-4 p-4 rounded-lg bg-elevated border border-strongline">
      <div ref={ref} className="text-sm text-primary leading-relaxed" />
    </div>
  );
}

// ---------- main component ----------

interface Props {
  projectId: string;
  projectLabel?: string;
}

/**
 * Project Files tab — minimal built-in File Manager (Phase E).
 *
 * - Virtual folders (DB hierarchy; NO physical disk folders).
 * - Breadcrumb navigation: Project / Folder / Subfolder (each level clickable).
 * - Folders: create / open / rename / delete (backend 409 shown as a clear error).
 * - Files: upload to current folder / rename / move (incl. to Root) / delete /
 *   download / preview (md via shared MarkdownBlock, pdf via blob iframe,
 *   docx via JSZip read-only, else download fallback).
 * - Reuses the existing user_files storage + auth infrastructure. NO mock data,
 *   NO RAG — files never enter the LLM context.
 *
 * Phase I.1 UX additions (existing API only, no RAG, no storage changes):
 * - single selection; double-click opens (folder in, file preview/download);
 * - keyboard: Enter = open, F2 = rename, Delete = delete confirmation
 *   (guarded: disabled while any dialog is open or an input has focus);
 * - context menu (right-click / "…" button): file = Открыть/Скачать/
 *   Переименовать/Переместить/Удалить, folder = Открыть/Переименовать/
 *   Переместить/Удалить (existing actions, no new items);
 * - OS -> browser drag & drop upload into the CURRENT folder (existing
 *   upload API; server-side 45MB/50MB limits unchanged) with a visual
 *   drop state;
 * - internal drag & drop move: file -> folder (existing PATCH move) and
 *   folder -> folder (new PATCH parent_folder_id, D1) with self/descendant
 *   cycle guards (client + server). Multi-select is DEFERRED.
 */
export function ProjectFiles({ projectId, projectLabel = "Проект" }: Props) {
  const [page, setPage] = useState<ProjectFilesPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Breadcrumb path of folder ids from root down to the current folder
  // (empty = we are at root).
  const [path, setPath] = useState<ProjectFolderItem[]>([]);
  const currentFolder = path.length ? path[path.length - 1] : null;
  const currentFolderId = currentFolder?.id ?? null;

  const [uploading, setUploading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // folder create / rename inline
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [busy, setBusy] = useState(false);

  // file operations
  const [renameTarget, setRenameTarget] = useState<ProjectFileItem | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [moveTarget, setMoveTarget] = useState<ProjectFileItem | null>(null);
  const [confirmFile, setConfirmFile] = useState<ProjectFileItem | null>(null);
  const [confirmFolder, setConfirmFolder] = useState<ProjectFolderItem | null>(null);

  // preview
  const [previewFile, setPreviewFile] = useState<ProjectFileItem | null>(null);

  // folder rename inline (state declared here — used by anyDialogOpen below)
  const [folderRename, setFolderRename] = useState<ProjectFolderItem | null>(null);
  const [folderRenameValue, setFolderRenameValue] = useState("");

  // ---------- Phase I.1 UX state ----------
  // Single selection (multi-select / Ctrl+A is DEFERRED by owner decision D2).
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // OS -> browser drag & drop upload: overlay state + progress.
  const [osDragOver, setOsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  // Internal drag & drop: what is being dragged (kind + id).
  const [dragItem, setDragItem] = useState<{ kind: "file" | "folder"; id: string } | null>(null);
  const [dragOverTarget, setDragOverTarget] = useState<string | null>(null); // folder id or "root"
  // Context menu (right-click / "…"): anchor + which item.
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; kind: "file" | "folder"; id: string } | null>(null);
  // Folder-move dialog (Phase I.1 D1; the file-move dialog stays as-is).
  const [moveFolderTarget, setMoveFolderTarget] = useState<ProjectFolderItem | null>(null);
  // Keyboard target: the list container is focusable so Enter/F2/Delete reach
  // onListKeyDown after a row click (row clicks focus the container).
  const listRef = useRef<HTMLDivElement>(null);

  const anyDialogOpen =
    creatingFolder ||
    !!renameTarget ||
    !!moveTarget ||
    !!moveFolderTarget ||
    !!confirmFile ||
    !!confirmFolder ||
    !!folderRename ||
    !!previewFile;

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchProjectFiles(projectId, currentFolderId)
      .then((p) => {
        setPage(p);
      })
      .catch((e) =>
        setError(isProjectNotFound(e) ? projectNotFoundMessage() : e instanceof Error ? e.message : "Не удалось загрузить файлы"),
      )
      .finally(() => setLoading(false));
  }, [projectId, currentFolderId]);

  useEffect(() => {
    load();
  }, [load]);

  // Load the full folder tree for the Move dialog (lazy, on open).
  const [moveTree, setMoveTree] = useState<ProjectFolderItem[] | null>(null);
  const loadMoveTree = useCallback(async () => {
    // Gather every folder of the project by walking each level. Root first,
    // then recurse by parent. The backend lists one level at a time.
    const all: ProjectFolderItem[] = [];
    const walk = async (parentId: string | null) => {
      const kids = await fetchProjectFolders(projectId, parentId);
      all.push(...kids);
      for (const k of kids) await walk(k.id);
    };
    try {
      await walk(null);
      setMoveTree(all);
    } catch {
      setMoveTree([]);
    }
  }, [projectId]);

  useEffect(() => {
    if (moveTarget && moveTree === null) void loadMoveTree();
  }, [moveTarget, moveTree, loadMoveTree]);

  useEffect(() => {
    if (moveFolderTarget && moveTree === null) void loadMoveTree();
  }, [moveFolderTarget, moveTree, loadMoveTree]);

  const dismissActionError = () => setActionError(null);

  // ---------- folder navigation ----------
  const openFolder = (f: ProjectFolderItem) => {
    setPath((prev) => [...prev, f]);
    setSelectedId(null);
  };
  const gotoCrumb = (index: number) => {
    // index -1 => root; otherwise keep crumbs[0..index]
    setPath((prev) => (index < 0 ? [] : prev.slice(0, index + 1)));
    setSelectedId(null);
  };
  const goBack = () => {
    setPath((prev) => prev.slice(0, -1));
    setSelectedId(null);
  };

  // ---------- folder actions ----------
  const doCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) {
      setCreatingFolder(false);
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      await createProjectFolder(projectId, name, currentFolderId);
      setNewFolderName("");
      setCreatingFolder(false);
      load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Не удалось создать папку");
    } finally {
      setBusy(false);
    }
  };

  const doRenameFolder = async () => {
    if (!folderRename) return;
    const name = folderRenameValue.trim();
    if (!name) return;
    setBusy(true);
    setActionError(null);
    try {
      await renameProjectFolder(projectId, folderRename.id, name);
      setFolderRename(null);
      load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Не удалось переименовать папку");
    } finally {
      setBusy(false);
    }
  };

  const doDeleteFolder = async () => {
    if (!confirmFolder) return;
    setBusy(true);
    setActionError(null);
    try {
      await deleteProjectFolder(projectId, confirmFolder.id);
      setConfirmFolder(null);
      // if we deleted the current folder, go up
      if (currentFolderId === confirmFolder.id) goBack();
      else load();
    } catch (e) {
      // 409 "Папка не пуста." surfaces verbatim (owner contract).
      setActionError(e instanceof Error ? e.message : "Не удалось удалить папку");
      setConfirmFolder(null);
    } finally {
      setBusy(false);
    }
  };

  // ---------- file actions ----------
  const handleFiles = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    setUploading(true);
    setActionError(null);
    try {
      // Upload sequentially into the CURRENT folder (shared account quota; the
      // backend enforces it and returns 413 on the first overflow).
      for (const f of Array.from(list)) {
        await uploadProjectFile(projectId, f, currentFolderId);
      }
      load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Не удалось загрузить файл");
      load();
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const doRenameFile = async () => {
    if (!renameTarget) return;
    const name = renameValue.trim();
    if (!name) return;
    setBusy(true);
    setActionError(null);
    try {
      await updateProjectFile(projectId, renameTarget.id, { name });
      setRenameTarget(null);
      load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Не удалось переименовать файл");
    } finally {
      setBusy(false);
    }
  };

  const doMoveFile = async (targetFolderId: string | null) => {
    if (!moveTarget) return;
    setBusy(true);
    setActionError(null);
    try {
      await updateProjectFile(projectId, moveTarget.id, { folder_id: targetFolderId });
      setMoveTarget(null);
      // If the file moved OUT of the current view, refresh the current level.
      load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Не удалось переместить файл");
    } finally {
      setBusy(false);
    }
  };

  const doDownload = async (f: ProjectFileItem) => {
    try {
      const blob = await downloadProjectFile(projectId, f.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Не удалось скачать файл");
    }
  };

  const doDeleteFile = async () => {
    if (!confirmFile) return;
    setBusy(true);
    setActionError(null);
    try {
      await deleteProjectFile(projectId, confirmFile.id);
      setConfirmFile(null);
      load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Не удалось удалить файл");
      setConfirmFile(null);
    } finally {
      setBusy(false);
    }
  };

  // ---------- derived ----------
  const folders = page?.folders ?? [];
  const files = page?.files ?? [];
  const isEmpty = folders.length === 0 && files.length === 0;

  // ---------- Phase I.1: internal drag & drop move ----------
  // Client cycle guard: is `targetId` the folder `draggedId` or one of its
  // descendants? (Server enforces the same guard — defense in depth.)
  const isDescendantOfDragged = (draggedId: string, targetId: string): boolean => {
    if (draggedId === targetId) return true;
    const find = (id: string) =>
      folders.find((f) => f.id === id) ?? moveTree?.find((f) => f.id === id) ?? null;
    let cur = targetId;
    let hops = 0;
    while (cur && hops < 100) {
      const node = find(cur);
      if (!node) return false; // unknown — server will validate
      cur = node.parent_folder_id ?? "";
      if (cur === draggedId) return true;
      hops += 1;
    }
    return false;
  };

  const doDndFileMove = async (fileId: string, targetFolderId: string | null) => {
    setDragOverTarget(null);
    setDragItem(null);
    const f = files.find((x) => x.id === fileId);
    if (!f || f.folder_id === targetFolderId) return; // no-op
    setBusy(true);
    setActionError(null);
    try {
      await updateProjectFile(projectId, f.id, { folder_id: targetFolderId });
      load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Не удалось переместить файл");
    } finally {
      setBusy(false);
    }
  };

  const doDndFolderMove = async (folderId: string, targetFolderId: string | null) => {
    setDragOverTarget(null);
    setDragItem(null);
    const f = folders.find((x) => x.id === folderId);
    if (!f) return;
    // Cycle/self guard on the TARGET: the target must not be the dragged folder
    // itself or one of its descendants. (Server enforces the same guard —
    // defense in depth.) A null target (project root) can never be a cycle.
    if (targetFolderId && isDescendantOfDragged(folderId, targetFolderId)) {
      setActionError("Нельзя переместить папку в собственное поддерево");
      return;
    }
    if (f.parent_folder_id === targetFolderId) return; // no-op
    setBusy(true);
    setActionError(null);
    try {
      await moveProjectFolder(projectId, folderId, targetFolderId);
      // If the CURRENT folder (or an ancestor of it) was moved, the path must
      // be recomputed: drop everything from the moved folder downward.
      const idx = path.findIndex((p) => p.id === folderId);
      if (idx !== -1) {
        setPath(path.slice(0, idx));
        setSelectedId(null);
      }
      load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Не удалось переместить папку");
    } finally {
      setBusy(false);
    }
  };

  // ---------- Phase I.1: OS -> browser drag & drop upload ----------
  const onOsDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setOsDragOver(false);
    const dt = e.dataTransfer;
    if (!dt || dt.files.length === 0) return;
    // Only external file drops (not internal item drags).
    if (!Array.from(dt.types).includes("Files")) return;
    const list = Array.from(dt.files);
    setUploading(true);
    setActionError(null);
    let done = 0;
    let firstErr: string | null = null;
    for (const f of list) {
      setUploadProgress(`${done + 1} из ${list.length}: ${f.name}`);
      try {
        await uploadProjectFile(projectId, f, currentFolderId);
      } catch (err) {
        if (!firstErr) firstErr = err instanceof Error ? err.message : "Не удалось загрузить файл";
      }
      done += 1;
    }
    setUploadProgress(null);
    setUploading(false);
    if (firstErr) setActionError(firstErr);
    load();
  };
  const hasFilesDrag = (e: React.DragEvent) =>
    Array.from(e.dataTransfer?.types ?? []).includes("Files");

  // Folder-move dialog confirm (D1) — reuses the same guarded move path.
  const doMoveFolderDialog = (targetFolderId: string | null) =>
    moveFolderTarget ? doDndFolderMove(moveFolderTarget.id, targetFolderId) : Promise.resolve();

  // ---------- Phase I.1: keyboard (Enter / F2 / Delete) ----------
  // Guarded: ignored while any dialog is open or focus is inside an
  // input/select/textarea (so typing in rename fields never triggers these).
  const openItemById = (id: string) => {
    const f = folders.find((x) => x.id === id);
    if (f) {
      openFolder(f);
      return;
    }
    const file = files.find((x) => x.id === id);
    if (!file) return;
    if (isPreviewable(file)) setPreviewFile(file);
    else void doDownload(file);
  };
  const renameItemById = (id: string) => {
    const f = folders.find((x) => x.id === id);
    if (f) {
      setFolderRename(f);
      setFolderRenameValue(f.name);
      return;
    }
    const file = files.find((x) => x.id === id);
    if (!file) return;
    setRenameTarget(file);
    setRenameValue(file.name);
  };
  const deleteItemById = (id: string) => {
    const f = folders.find((x) => x.id === id);
    if (f) {
      setConfirmFolder(f);
      return;
    }
    const file = files.find((x) => x.id === id);
    if (file) setConfirmFile(file);
  };

  const onListKeyDown = (e: React.KeyboardEvent) => {
    if (!selectedId || anyDialogOpen) return;
    const t = e.target as HTMLElement;
    if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return;
    if (e.key === "Enter") {
      e.preventDefault();
      openItemById(selectedId);
    } else if (e.key === "F2") {
      e.preventDefault();
      renameItemById(selectedId);
    } else if (e.key === "Delete") {
      e.preventDefault();
      deleteItemById(selectedId);
    }
  };

  // ---------- Phase I.1: context menu actions (existing handlers only) ----------
  const ctxItems = useMemo(() => {
    if (!ctxMenu) return [];
    if (ctxMenu.kind === "file") {
      const f = files.find((x) => x.id === ctxMenu.id);
      if (!f) return [];
      return [
        { label: "Открыть", icon: Eye, danger: false, run: () => (isPreviewable(f) ? setPreviewFile(f) : void doDownload(f)) },
        { label: "Скачать", icon: Download, danger: false, run: () => void doDownload(f) },
        { label: "Переименовать", icon: Pencil, danger: false, run: () => { setRenameTarget(f); setRenameValue(f.name); } },
        { label: "Переместить", icon: MoveRight, danger: false, run: () => setMoveTarget(f) },
        { label: "Удалить", icon: Trash2, danger: true, run: () => setConfirmFile(f) },
      ];
    }
    const f = folders.find((x) => x.id === ctxMenu.id);
    if (!f) return [];
    return [
      { label: "Открыть", icon: FolderOpen, danger: false, run: () => openFolder(f) },
      { label: "Переименовать", icon: Pencil, danger: false, run: () => { setFolderRename(f); setFolderRenameValue(f.name); } },
      { label: "Переместить", icon: MoveRight, danger: false, run: () => setMoveFolderTarget(f) },
      { label: "Удалить", icon: Trash2, danger: true, run: () => setConfirmFolder(f) },
    ];
  }, [ctxMenu, files, folders]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close context menu on outside click / Escape.
  useEffect(() => {
    if (!ctxMenu) return;
    const onDocClick = (ev: MouseEvent) => {
      const el = ev.target as HTMLElement;
      if (el?.closest?.("[data-ctxmenu]")) return;
      setCtxMenu(null);
    };
    const onEsc = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") setCtxMenu(null);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    window.addEventListener("scroll", () => setCtxMenu(null), true);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
      window.removeEventListener("scroll", () => setCtxMenu(null), true);
    };
  }, [ctxMenu]);

  // Build a depth map for the move dialog (folder id -> depth).
  const depthMap = useMemo(() => {
    const m = new Map<string, number>();
    if (moveTree) {
      const depth = (parentId: string | null): number => {
        let d = 0;
        let cur = parentId;
        while (cur) {
          const p = moveTree.find((f) => f.id === cur);
          if (!p) break;
          d += 1;
          cur = p.parent_folder_id;
        }
        return d;
      };
      for (const f of moveTree) m.set(f.id, depth(f.parent_folder_id));
    }
    return m;
  }, [moveTree]);

  // ---------- render ----------

  if (loading && !page) {
    return (
      <div className="py-10 flex justify-center text-tertiary">
        <Loader2 size={18} className="animate-spin" />
      </div>
    );
  }
  if (error && !page) {
    return (
      <div className="py-10 text-center">
        <p className="text-sm text-tertiary">{error}</p>
        <button onClick={load} className="mt-3 h-8 px-3 rounded-lg text-xs text-accent hover:bg-hoverbg transition-colors">
          Повторить
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb + actions */}
      <div className="flex items-center justify-between mb-3 gap-2">
        <nav className="flex items-center gap-1 min-w-0" aria-label="Папки">
          <button
            onClick={() => gotoCrumb(-1)}
            className={cn(
              "inline-flex items-center gap-1 px-2 h-7 rounded-lg text-xs transition-colors max-w-[140px]",
              path.length === 0 ? "text-primary font-medium" : "text-secondary hover:text-primary hover:bg-hoverbg",
            )}
          >
            <Home size={13} className="shrink-0" />
            <span className="truncate">{projectLabel}</span>
          </button>
          {path.map((f, i) => (
            <span key={f.id} className="flex items-center gap-1 min-w-0">
              <ChevronRight size={13} className="text-tertiary shrink-0" />
              <button
                onClick={() => gotoCrumb(i)}
                className={cn(
                  "inline-flex items-center gap-1 px-2 h-7 rounded-lg text-xs transition-colors max-w-[140px]",
                  i === path.length - 1 ? "text-primary font-medium" : "text-secondary hover:text-primary hover:bg-hoverbg",
                )}
              >
                <span className="truncate">{f.name}</span>
              </button>
            </span>
          ))}
        </nav>
        <div className="flex items-center gap-2 shrink-0">
          {path.length > 0 && (
            <Tip label="Назад" side="bottom">
              <button
                onClick={goBack}
                className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-xs text-secondary hover:text-primary hover:bg-hoverbg transition-colors"
              >
                <ArrowLeft size={14} /> Назад
              </button>
            </Tip>
          )}
          {creatingFolder ? (
            <div className="flex items-center gap-1">
              <input
                autoFocus
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void doCreateFolder();
                  if (e.key === "Escape") {
                    setCreatingFolder(false);
                    setNewFolderName("");
                  }
                }}
                placeholder="Имя папки"
                className="h-8 w-40 px-2.5 rounded-lg text-xs bg-elevated border border-strongline text-primary outline-none focus:border-accent"
              />
              <button
                onClick={() => void doCreateFolder()}
                disabled={busy}
                className="p-1.5 rounded-lg text-accent hover:bg-hoverbg transition-colors"
                aria-label="Создать"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              </button>
              <button
                onClick={() => {
                  setCreatingFolder(false);
                  setNewFolderName("");
                }}
                disabled={busy}
                className="p-1.5 rounded-lg text-secondary hover:bg-hoverbg transition-colors"
                aria-label="Отмена"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setCreatingFolder(true)}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs text-secondary hover:text-primary hover:bg-hoverbg transition-colors"
            >
              <FolderPlus size={13} /> Новая папка
            </button>
          )}
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs text-accent hover:bg-hoverbg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            Загрузить
          </button>
          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => void handleFiles(e.target.files)}
          />
        </div>
      </div>

      {actionError && (
        <div className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300 flex items-start justify-between gap-3" role="alert">
          <span>{actionError}</span>
          <button onClick={dismissActionError} className="shrink-0 text-red-300/70 hover:text-red-300" aria-label="Закрыть">
            <X size={13} />
          </button>
        </div>
      )}

      {loading && (
        <div className="py-8 flex justify-center text-tertiary">
          <Loader2 size={16} className="animate-spin" />
        </div>
      )}

      {!loading && (
        <>
          {isEmpty ? (
            <div className="py-16 flex flex-col items-center gap-3 text-center">
              <Folder size={28} className="text-tertiary" />
              <p className="text-sm text-secondary">
                {currentFolder ? `В папке «${currentFolder.name}» пока пусто.` : "В этом проекте пока нет файлов."}
              </p>
              <button
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-sm text-accent hover:bg-hoverbg transition-colors disabled:opacity-50"
              >
                <Upload size={14} /> Загрузить файл
              </button>
            </div>
          ) : (
            <div
              onDragOver={(e) => {
                // OS file drop into the CURRENT folder (Phase I.1).
                if (!hasFilesDrag(e)) return;
                e.preventDefault();
                setOsDragOver(true);
              }}
              onDragLeave={(e) => {
                // Only clear when leaving the container itself (not children).
                if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                setOsDragOver(false);
              }}
              onDrop={(e) => {
                if (!hasFilesDrag(e)) return;
                e.preventDefault();
                void onOsDrop(e);
              }}
              onKeyDown={onListKeyDown}
              tabIndex={0}
              ref={listRef}
              className="relative outline-none focus:outline-none"
            >
              {osDragOver && (
                <div className="absolute inset-0 z-20 rounded-xl border-2 border-dashed border-accent bg-accent/10 flex items-center justify-center pointer-events-none" data-testid="dnd-overlay">
                  <div className="flex flex-col items-center gap-2 text-accent">
                    <Upload size={22} />
                    <span className="text-sm font-medium">
                      Отпустите файлы, чтобы загрузить{currentFolder ? ` в «${currentFolder.name}»` : ""}
                    </span>
                  </div>
                </div>
              )}
              {uploadProgress && (
                <div className="absolute inset-x-0 top-0 z-20 flex justify-center pointer-events-none">
                  <div className="mt-2 px-3 py-1.5 rounded-lg bg-elevated border border-strongline text-xs text-primary shadow-lg flex items-center gap-2">
                    <Loader2 size={13} className="animate-spin text-accent" />
                    Загрузка: {uploadProgress}
                  </div>
                </div>
              )}
              <ul className="space-y-2">
              {/* Folders first, then files (matches backend ordering). */}
              {folders.map((f) => (
                <li
                  key={f.id}
                  draggable
                  onDragStart={(e) => {
                    setDragItem({ kind: "folder", id: f.id });
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", f.id);
                  }}
                  onDragEnd={() => {
                    setDragItem(null);
                    setDragOverTarget(null);
                  }}
                  onDragOver={(e) => {
                    // Folder is a drop target only while something is dragged
                    // AND it is not the dragged folder's own subtree.
                    if (!dragItem) return;
                    if (isDescendantOfDragged(dragItem.id, f.id)) return; // cycle
                    e.preventDefault();
                    setDragOverTarget(f.id);
                  }}
                  onDragLeave={(e) => {
                    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                    setDragOverTarget((cur) => (cur === f.id ? null : cur));
                  }}
                  onDrop={(e) => {
                    if (!dragItem) return;
                    e.preventDefault();
                    e.stopPropagation();
                    if (dragItem.kind === "file") {
                      void doDndFileMove(dragItem.id, f.id);
                    } else {
                      void doDndFolderMove(dragItem.id, f.id);
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedId(f.id);
                    listRef.current?.focus();
                  }}
                  onDoubleClick={() => openFolder(f)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setSelectedId(f.id);
                    setCtxMenu({ x: Math.min(e.clientX, window.innerWidth - 200), y: Math.min(e.clientY, window.innerHeight - 240), kind: "folder", id: f.id });
                  }}
                  className={cn(
                    "group flex items-center gap-3 rounded-xl border p-3 transition-colors",
                    dragItem && dragOverTarget === f.id && "border-accent ring-1 ring-accent",
                    !dragOverTarget && selectedId === f.id && "border-accent bg-hoverbg",
                    !dragItem && selectedId !== f.id && "border-hairline bg-hoverbg/40 hover:bg-hoverbg",
                    dragItem && dragOverTarget !== f.id && selectedId !== f.id && "border-hairline bg-hoverbg/40",
                  )}
                >
                  <span className="w-10 h-10 rounded-lg bg-elevated border border-strongline flex items-center justify-center shrink-0">
                    <Folder size={16} className="text-accent" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-primary truncate" title={f.name}>
                      {f.name}
                    </div>
                    <div className="text-[11px] text-tertiary">Папка · {fmtDate(f.created_at)}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Tip label="Открыть" side="top">
                      <button
                        onClick={() => openFolder(f)}
                        className="p-1.5 rounded-lg text-secondary hover:text-primary hover:bg-hoverbg2 transition-colors"
                      >
                        <FolderOpen size={14} />
                      </button>
                    </Tip>
                    <Tip label="Переименовать" side="top">
                      <button
                        onClick={() => {
                          setFolderRename(f);
                          setFolderRenameValue(f.name);
                        }}
                        className="p-1.5 rounded-lg text-secondary hover:text-primary hover:bg-hoverbg2 transition-colors"
                      >
                        <Pencil size={14} />
                      </button>
                    </Tip>
                    <Tip label="Удалить" side="top">
                      <button
                        onClick={() => setConfirmFolder(f)}
                        className="p-1.5 rounded-lg text-secondary hover:text-red-400 hover:bg-hoverbg2 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </Tip>
                    <Tip label="Ещё" side="top">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedId(f.id);
                          const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                          setCtxMenu({ x: Math.min(r.right - 180, window.innerWidth - 200), y: Math.min(r.bottom + 4, window.innerHeight - 240), kind: "folder", id: f.id });
                        }}
                        className="p-1.5 rounded-lg text-secondary hover:text-primary hover:bg-hoverbg2 transition-colors"
                        aria-label="Ещё"
                      >
                        <MoreHorizontal size={14} />
                      </button>
                    </Tip>
                  </div>
                </li>
              ))}

              {files.map((f) => (
                <li
                  key={f.id}
                  draggable
                  onDragStart={(e) => {
                    setDragItem({ kind: "file", id: f.id });
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", f.id);
                  }}
                  onDragEnd={() => {
                    setDragItem(null);
                    setDragOverTarget(null);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedId(f.id);
                    listRef.current?.focus();
                  }}
                  onDoubleClick={() => {
                    if (isPreviewable(f)) setPreviewFile(f);
                    else void doDownload(f);
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setSelectedId(f.id);
                    setCtxMenu({ x: Math.min(e.clientX, window.innerWidth - 200), y: Math.min(e.clientY, window.innerHeight - 240), kind: "file", id: f.id });
                  }}
                  className={cn(
                    "group flex items-center gap-3 rounded-xl border p-3 transition-colors cursor-default",
                    selectedId === f.id ? "border-accent bg-hoverbg" : "border-hairline bg-hoverbg/40 hover:bg-hoverbg",
                  )}
                >
                  <span className="w-10 h-10 rounded-lg bg-elevated border border-strongline flex items-center justify-center shrink-0">
                    {fileIcon(f.kind, f.name)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-primary truncate" title={f.name}>
                      {f.name}
                    </div>
                    <div className="text-[11px] text-tertiary">
                      {ext(f.name)} · {formatSize(f.size)} · создан {fmtDate(f.created_at)}
                      {f.updated_at && f.updated_at !== f.created_at ? ` · изм. ${fmtDate(f.updated_at)}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {isPreviewable(f) && (
                      <Tip label="Предпросмотр" side="top">
                        <button
                          onClick={() => setPreviewFile(f)}
                          className="p-1.5 rounded-lg text-secondary hover:text-primary hover:bg-hoverbg2 transition-colors"
                        >
                          <Eye size={14} />
                        </button>
                      </Tip>
                    )}
                    <Tip label="Скачать" side="top">
                      <button
                        onClick={() => void doDownload(f)}
                        className="p-1.5 rounded-lg text-secondary hover:text-primary hover:bg-hoverbg2 transition-colors"
                      >
                        <Download size={14} />
                      </button>
                    </Tip>
                    <Tip label="Переименовать" side="top">
                      <button
                        onClick={() => {
                          setRenameTarget(f);
                          setRenameValue(f.name);
                        }}
                        className="p-1.5 rounded-lg text-secondary hover:text-primary hover:bg-hoverbg2 transition-colors"
                      >
                        <Pencil size={14} />
                      </button>
                    </Tip>
                    <Tip label="Переместить" side="top">
                      <button
                        onClick={() => setMoveTarget(f)}
                        className="p-1.5 rounded-lg text-secondary hover:text-primary hover:bg-hoverbg2 transition-colors"
                      >
                        <MoveRight size={14} />
                      </button>
                    </Tip>
                    <Tip label="Удалить" side="top">
                      <button
                        onClick={() => setConfirmFile(f)}
                        className="p-1.5 rounded-lg text-secondary hover:text-red-400 hover:bg-hoverbg2 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </Tip>
                    <Tip label="Ещё" side="top">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedId(f.id);
                          const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                          setCtxMenu({ x: Math.min(r.right - 180, window.innerWidth - 200), y: Math.min(r.bottom + 4, window.innerHeight - 240), kind: "file", id: f.id });
                        }}
                        className="p-1.5 rounded-lg text-secondary hover:text-primary hover:bg-hoverbg2 transition-colors"
                        aria-label="Ещё"
                      >
                        <MoreHorizontal size={14} />
                      </button>
                    </Tip>
                  </div>
                </li>
              ))}
            </ul>
            </div>
          )}
        </>
      )}

      {/* ---- Create-folder is inline above ---- */}

      {/* ---- Rename folder dialog ---- */}
      {folderRename && (
        <Dialog title="Переименовать папку" onClose={() => setFolderRename(null)}>
          <input
            autoFocus
            value={folderRenameValue}
            onChange={(e) => setFolderRenameValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void doRenameFolder()}
            className="w-full h-9 px-3 rounded-lg text-sm bg-elevated border border-strongline text-primary outline-none focus:border-accent"
          />
          <DialogActions busy={busy}>
            <button onClick={() => setFolderRename(null)} disabled={busy} className={cancelBtn}>
              Отмена
            </button>
            <button onClick={() => void doRenameFolder()} disabled={busy || !folderRenameValue.trim()} className={primaryBtn}>
              {busy && <Loader2 size={14} className="animate-spin" />} Сохранить
            </button>
          </DialogActions>
        </Dialog>
      )}

      {/* ---- Rename file dialog ---- */}
      {renameTarget && (
        <Dialog title="Переименовать файл" onClose={() => setRenameTarget(null)}>
          <input
            autoFocus
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void doRenameFile()}
            className="w-full h-9 px-3 rounded-lg text-sm bg-elevated border border-strongline text-primary outline-none focus:border-accent"
          />
          <DialogActions busy={busy}>
            <button onClick={() => setRenameTarget(null)} disabled={busy} className={cancelBtn}>
              Отмена
            </button>
            <button onClick={() => void doRenameFile()} disabled={busy || !renameValue.trim()} className={primaryBtn}>
              {busy && <Loader2 size={14} className="animate-spin" />} Сохранить
            </button>
          </DialogActions>
        </Dialog>
      )}

      {/* ---- Move file dialog ---- */}
      {moveTarget && (
        <Dialog title={`Переместить «${moveTarget.name}»`} onClose={() => setMoveTarget(null)}>
          {moveTree === null ? (
            <div className="py-8 flex justify-center text-tertiary">
              <Loader2 size={16} className="animate-spin" />
            </div>
          ) : (
            <div className="max-h-[45vh] overflow-auto -mx-1 px-1 space-y-0.5">
              <button
                onClick={() => void doMoveFile(null)}
                disabled={busy || moveTarget.folder_id == null}
                className={cn(
                  "w-full flex items-center gap-2 px-2.5 h-9 rounded-lg text-sm transition-colors text-left",
                  moveTarget.folder_id == null
                    ? "bg-hoverbg2 text-primary"
                    : "text-secondary hover:bg-hoverbg hover:text-primary",
                  (busy || moveTarget.folder_id == null) && "opacity-50 cursor-not-allowed",
                )}
              >
                <Home size={14} className="text-accent" />
                <span className="flex-1">Корень проекта</span>
                {moveTarget.folder_id == null && <Check size={14} className="text-accent" />}
              </button>
              {moveTree.map((f) => {
                const isCurrent = f.id === moveTarget.folder_id;
                const depth = depthMap.get(f.id) ?? 0;
                return (
                  <button
                    key={f.id}
                    onClick={() => void doMoveFile(f.id)}
                    disabled={busy || isCurrent}
                    style={{ paddingLeft: `${10 + depth * 16}px` }}
                    className={cn(
                      "w-full flex items-center gap-2 h-9 rounded-lg text-sm transition-colors text-left",
                      isCurrent ? "bg-hoverbg2 text-primary" : "text-secondary hover:bg-hoverbg hover:text-primary",
                      (busy || isCurrent) && "opacity-50 cursor-not-allowed",
                    )}
                  >
                    <Folder size={14} className="text-accent shrink-0" />
                    <span className="flex-1 truncate">{f.name}</span>
                    {isCurrent && <Check size={14} className="text-accent shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
          <DialogActions busy={false}>
            <button onClick={() => setMoveTarget(null)} className={cancelBtn}>
              Закрыть
            </button>
          </DialogActions>
        </Dialog>
      )}

      {/* ---- Move folder dialog (Phase I.1, D1) ---- */}
      {moveFolderTarget && (
        <Dialog title={`Переместить папку «${moveFolderTarget.name}»`} onClose={() => setMoveFolderTarget(null)}>
          {moveTree === null ? (
            <div className="py-8 flex justify-center text-tertiary">
              <Loader2 size={16} className="animate-spin" />
            </div>
          ) : (
            <div className="max-h-[45vh] overflow-auto -mx-1 px-1 space-y-0.5">
              <button
                onClick={() => void doMoveFolderDialog(null)}
                disabled={busy || moveFolderTarget.parent_folder_id == null}
                className={cn(
                  "w-full flex items-center gap-2 px-2.5 h-9 rounded-lg text-sm transition-colors text-left",
                  moveFolderTarget.parent_folder_id == null
                    ? "bg-hoverbg2 text-primary"
                    : "text-secondary hover:bg-hoverbg hover:text-primary",
                  (busy || moveFolderTarget.parent_folder_id == null) && "opacity-50 cursor-not-allowed",
                )}
              >
                <Home size={14} className="text-accent" />
                <span className="flex-1">Корень проекта</span>
                {moveFolderTarget.parent_folder_id == null && <Check size={14} className="text-accent" />}
              </button>
              {moveTree.map((f) => {
                const isCurrent = f.id === moveFolderTarget.parent_folder_id;
                const isCycle = f.id === moveFolderTarget.id || isDescendantOfDragged(moveFolderTarget.id, f.id);
                const depth = depthMap.get(f.id) ?? 0;
                return (
                  <button
                    key={f.id}
                    onClick={() => void doMoveFolderDialog(f.id)}
                    disabled={busy || isCurrent || isCycle}
                    title={isCycle ? "Нельзя переместить папку в собственное поддерево" : undefined}
                    style={{ paddingLeft: `${10 + depth * 16}px` }}
                    className={cn(
                      "w-full flex items-center gap-2 h-9 rounded-lg text-sm transition-colors text-left",
                      isCurrent ? "bg-hoverbg2 text-primary" : "text-secondary hover:bg-hoverbg hover:text-primary",
                      (busy || isCurrent || isCycle) && "opacity-50 cursor-not-allowed",
                    )}
                  >
                    <Folder size={14} className="text-accent shrink-0" />
                    <span className="flex-1 truncate">{f.name}</span>
                    {isCurrent && <Check size={14} className="text-accent shrink-0" />}
                    {isCycle && <X size={14} className="text-tertiary shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
          <DialogActions busy={false}>
            <button onClick={() => setMoveFolderTarget(null)} className={cancelBtn}>
              Закрыть
            </button>
          </DialogActions>
        </Dialog>
      )}

      {/* ---- Delete file confirm ---- */}
      {confirmFile && (
        <Dialog title="Удалить файл?" onClose={() => setConfirmFile(null)}>
          <p className="text-sm text-secondary break-all">
            «{confirmFile.name}» будет удалён из проекта.
          </p>
          <DialogActions busy={busy}>
            <button onClick={() => setConfirmFile(null)} disabled={busy} className={cancelBtn}>
              Отмена
            </button>
            <button onClick={() => void doDeleteFile()} disabled={busy} className={dangerBtn}>
              {busy && <Loader2 size={14} className="animate-spin" />} Удалить
            </button>
          </DialogActions>
        </Dialog>
      )}

      {/* ---- Delete folder confirm ---- */}
      {confirmFolder && (
        <Dialog title="Удалить папку?" onClose={() => setConfirmFolder(null)}>
          <p className="text-sm text-secondary break-all">
            «{confirmFolder.name}» будет удалена. Папку с файлами или подпапками удалить нельзя.
          </p>
          <DialogActions busy={busy}>
            <button onClick={() => setConfirmFolder(null)} disabled={busy} className={cancelBtn}>
              Отмена
            </button>
            <button onClick={() => void doDeleteFolder()} disabled={busy} className={dangerBtn}>
              {busy && <Loader2 size={14} className="animate-spin" />} Удалить
            </button>
          </DialogActions>
        </Dialog>
      )}

      {/* ---- Context menu (Phase I.1) ---- */}
      {ctxMenu && (
        <div
          data-ctxmenu
          className="fixed z-[120] min-w-[180px] rounded-xl border border-strongline bg-elevated shadow-2xl py-1 overflow-hidden"
          style={{ left: ctxMenu.x, top: ctxMenu.y }}
          role="menu"
        >
          {ctxItems.map((item) => (
            <button
              key={item.label}
              role="menuitem"
              onClick={() => {
                setCtxMenu(null);
                item.run();
              }}
              className={cn(
                "w-full flex items-center gap-2.5 px-3 h-9 text-sm transition-colors text-left",
                item.danger ? "text-red-400 hover:bg-red-500/10" : "text-primary hover:bg-hoverbg",
              )}
            >
              <item.icon size={14} className={item.danger ? "text-red-400" : "text-secondary"} />
              {item.label}
            </button>
          ))}
        </div>
      )}

      {/* ---- Preview modal ---- */}
      {previewFile && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setPreviewFile(null)} aria-hidden />
          <div className="relative w-full max-w-3xl max-h-[90vh] flex flex-col rounded-xl border border-strongline bg-elevated shadow-2xl overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-strongline shrink-0">
              <FileText size={15} className="text-accent shrink-0" />
              <span className="text-sm font-medium text-primary truncate flex-1">{previewFile.name}</span>
              <Tip label="Скачать" side="bottom">
                <button
                  onClick={() => void doDownload(previewFile)}
                  className="p-1.5 rounded-lg text-secondary hover:text-primary hover:bg-hoverbg2 transition-colors"
                >
                  <Download size={15} />
                </button>
              </Tip>
              <Tip label="Закрыть" side="bottom">
                <button
                  onClick={() => setPreviewFile(null)}
                  className="p-1.5 rounded-lg text-secondary hover:text-primary hover:bg-hoverbg2 transition-colors"
                >
                  <X size={16} />
                </button>
              </Tip>
            </div>
            <div className="p-4 overflow-hidden">
              <PreviewBody projectId={projectId} file={previewFile} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- small presentational helpers ----------

const cancelBtn =
  "h-9 px-4 rounded-lg text-sm text-secondary hover:bg-hoverbg2 hover:text-primary transition-colors";
const primaryBtn =
  "h-9 px-4 rounded-lg text-sm font-medium text-white bg-accent/90 hover:bg-accent transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed";
const dangerBtn =
  "h-9 px-4 rounded-lg text-sm font-medium text-white bg-red-500/90 hover:bg-red-500 transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed";

function Dialog({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[105] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-md rounded-xl border border-strongline bg-elevated p-5 shadow-2xl">
        <h3 className="text-base font-medium text-primary mb-4 truncate">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function DialogActions({ children, busy }: { children: React.ReactNode; busy?: boolean }) {
  return <div className="mt-5 flex justify-end gap-2">{children}</div>;
}
